'use client';
import type { RefObject, SyntheticEvent } from 'react';
import React, { useRef } from 'react';

import { Text } from '~components/Typography';
import { useDialogs } from '~entities/dialogs';
import { useNavigationLinks } from '~hooks/useNavigationLinks';
import RightArrowIcon from '~public/assets/icons/navigation/rightArrow.svg';
import { CounterBadge } from '~shared/ui/atoms';
import { useHeaderContext } from '~widgets/Header/context';
import { MenuDropdown } from '~widgets/Header/ui/MenuDropdown';

import { Container, TabLink, unreadMessagesClass } from './styles';

const Tab = ({
	href,
	className,
	active,
	handle,
	icon,
	text,
	asButton = false,
	unreadMessagesCount = 0,
	...props
}: {
	ref?: RefObject<HTMLAnchorElement>;
	href?: string;
	className?: string;
	active: boolean;
	icon: React.ReactNode;
	text: string;
	asButton?: boolean;
	order?: number;
	unreadMessagesCount?: number;
	handle: (e: SyntheticEvent) => void;
}) => (
	<TabLink
		className={className}
		as={asButton ? 'button' : undefined}
		href={href as string}
		active={active ? 1 : 0}
		onClick={handle}
		{...props}
	>
		{icon}
		<Text size="S">{text}</Text>
		{unreadMessagesCount > 0 && (
			<CounterBadge
				className={unreadMessagesClass}
				count={unreadMessagesCount}
			/>
		)}
	</TabLink>
);

export const NavigationBar = () => {
	const { toggleSideMenu } = useHeaderContext();
	const links = useNavigationLinks();
	const { unreadMessagesCount } = useDialogs();
	const dropdownRef = useRef<HTMLAnchorElement>(null);

	return (
		<Container id="navigation-bar">
			{links.map((link) =>
				'children' in link ? (
					<React.Fragment key={link.text}>
						<Tab
							data-name={link['data-name']}
							ref={dropdownRef as RefObject<HTMLAnchorElement>}
							asButton={true}
							active={link.active}
							handle={link.handle}
							icon={link.icon}
							text={link.text}
						/>
						<MenuDropdown
							placement="top"
							items={link.children}
							targetRef={dropdownRef}
						/>
					</React.Fragment>
				) : (
					<Tab
						data-name={link['data-name']}
						unreadMessagesCount={
							link.withMessageCounter ? unreadMessagesCount : undefined
						}
						key={link.path}
						href={link.path}
						active={link.active}
						handle={link.handle}
						icon={link.icon}
						text={link.text}
						order={link.mobileOrder}
					/>
				)
			)}
			<Tab
				// class to ignore this element handling in useOnClickOutside
				className="ignore-click-sidemenu"
				active={false}
				handle={toggleSideMenu}
				icon={<RightArrowIcon />}
				text="More"
				asButton={true}
				order={4}
			/>
		</Container>
	);
};
